// Test runner: gated behind SSO + tester group. Lets a tester multi-select
// models, configure "auto-advance" + delay, and walk through the benchmark run
// one model at a time with a progress ribbon.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  api,
  loginUrl,
  type AppConfig,
  type BatchState,
  type CatalogModel,
  type ColorMetric,
  type MatrixOverrides,
  type ModelResult,
} from '../api';
import { useApp } from '../lib/appContext';
import { METRIC_LABEL } from '../lib/colorScale';
import Heatmap from '../components/Heatmap';
import PhasePanel from '../components/PhasePanel';
import MatrixTimingTable from '../components/MatrixTimingTable';
import ModelRibbon from '../components/ModelRibbon';
import RunActivity, { type ActivityState } from '../components/RunActivity';

const BATCH_KEY = 'aiperf.activeBatch';

interface ParamState {
  inputTokens: string;
  outputTokens: string;
  repetitions: number;
  safetyMargin: number;
  colorMetric: ColorMetric;
  kvMode: 'auto' | 'fixed';
  kvValue: number;
}

function defaultParams(c: AppConfig): ParamState {
  return {
    inputTokens: c.matrix.inputTokens.join(', '),
    outputTokens: c.matrix.outputTokens.join(', '),
    repetitions: c.matrix.repetitions,
    safetyMargin: c.context.safetyMargin,
    colorMetric: c.matrix.colorMetric,
    kvMode: 'auto',
    kvValue: c.context.defaultMaxKv,
  };
}

function parseCsvInts(s: string): number[] {
  return s
    .split(/[\s,]+/)
    .map((x) => Number(x))
    .filter((n) => Number.isFinite(n) && n > 0)
    .map((n) => Math.round(n));
}

export default function RunPage() {
  const { config, user } = useApp();

  if (!user) {
    return (
      <section className="gate">
        <h1>Sign in to run tests</h1>
        <p className="muted">
          Results are public. Starting new tests requires an SSO sign-in.
        </p>
        <a className="btn" href={loginUrl}>
          Sign in with SSO
        </a>
      </section>
    );
  }

  if (!user.isTester) {
    return (
      <section className="gate">
        <h1>Not authorised</h1>
        <p className="muted">
          You are signed in, but not a member of the required group
          {config?.auth.testerGroup ? ` “${config.auth.testerGroup}”` : ''}. Tests cannot be started.
        </p>
      </section>
    );
  }

  return <Runner />;
}

function Runner() {
  const { config, setHardware } = useApp();
  const [models, setModels] = useState<CatalogModel[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [autoAdvance, setAutoAdvance] = useState(config?.run.autoAdvanceDefault ?? false);
  const [delaySeconds, setDelaySeconds] = useState(config?.run.autoAdvanceDelaySeconds ?? 5);
  const [hardwareDraft, setHardwareDraft] = useState(config?.hardware ?? '');

  const [batch, setBatch] = useState<BatchState | null>(null);
  const [live, setLive] = useState<ModelResult | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [catalogWarning, setCatalogWarning] = useState<string | null>(null);
  const [params, setParams] = useState<ParamState | null>(null);
  const [activity, setActivity] = useState<ActivityState | null>(null);
  const liveTimer = useRef<number | null>(null);

  useEffect(() => {
    if (config && !params) setParams(defaultParams(config));
  }, [config, params]);
  useEffect(() => {
    if (config?.hardware) setHardwareDraft((d) => d || config.hardware || '');
  }, [config]);

  const saveHardware = () => {
    const label = hardwareDraft.trim();
    if (label === (config?.hardware ?? '')) return;
    api
      .setHardware(label)
      .then((r) => {
        setHardware(r.hardware);
        setHardwareDraft(r.hardware ?? '');
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to save hardware'));
  };

  // --- initial load: catalogue + adopt an in-flight batch from the server ---
  useEffect(() => {
    api
      .models()
      .then((r) => {
        setModels(r.models);
        setCatalogWarning(r.warning);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load models'));

    // The worker keeps running server-side regardless of navigation; pick the
    // current batch back up so a run in progress is never "lost".
    api
      .currentBatch()
      .then((r) => {
        const b = r.batch;
        if (!b) return;
        const startedHere = sessionStorage.getItem(BATCH_KEY) === b.id;
        if (b.status === 'running' || startedHere) {
          sessionStorage.setItem(BATCH_KEY, b.id);
          setBatch(b);
        }
      })
      .catch(() => undefined);
  }, []);

  const currentRunId = batch?.currentRun?.id ?? null;
  const currentModel = batch ? batch.models[batch.currentIndex]?.model : undefined;
  const isLast = batch ? batch.currentIndex >= batch.models.length - 1 : false;
  const currentFinished =
    batch?.currentRun?.status === 'done' || batch?.currentRun?.status === 'error';

  // Matrix axes: prefer live results, then the running batch's snapshot, then defaults.
  const axInput =
    live?.inputTokens ??
    batch?.currentRun?.config_snapshot.inputTokens ??
    config?.matrix.inputTokens ??
    [];
  const axOutput =
    live?.outputTokens ??
    batch?.currentRun?.config_snapshot.outputTokens ??
    config?.matrix.outputTokens ??
    [];

  const refetchBatch = useCallback(async () => {
    if (!batch) return;
    try {
      setBatch(await api.batch(batch.id));
    } catch {
      /* keep last known */
    }
  }, [batch]);

  const doFetchLive = useCallback(() => {
    if (!currentModel || !currentRunId) return;
    api
      .modelResult(currentModel, currentRunId)
      .then(setLive)
      .catch(() => undefined);
  }, [currentModel, currentRunId]);

  const refetchLive = useCallback(() => {
    if (liveTimer.current) return; // simple throttle
    liveTimer.current = window.setTimeout(() => {
      liveTimer.current = null;
    }, 800);
    doFetchLive();
  }, [doFetchLive]);

  // --- live progress stream for the model under test ---
  useEffect(() => {
    if (!currentRunId) return;
    setLive(null);
    setProgress(null);
    setActivity(null);
    // The SSE stream only carries *future* events — pull the current partial
    // matrix right away so reopening the page shows where the run is.
    doFetchLive();
    const es = new EventSource(`/api/runs/${currentRunId}/stream`);

    const parse = (ev: MessageEvent) => {
      try {
        return JSON.parse(ev.data) as {
          progress?: { done: number; total: number };
          payload?: Record<string, unknown>;
        };
      } catch {
        return {};
      }
    };
    const bump = (phase: ActivityState['phase'], d: ReturnType<typeof parse>) =>
      setActivity((a) => {
        const p = d.payload ?? {};
        const cellChanged =
          p.inputTokens !== undefined &&
          (p.inputTokens !== a?.input || p.outputTokens !== a?.output || p.repetition !== a?.repetition);
        return {
          phase,
          model: (p.model as string) ?? a?.model,
          input: (p.inputTokens as number) ?? a?.input,
          output: (p.outputTokens as number) ?? a?.output,
          repetition: (p.repetition as number) ?? a?.repetition,
          repetitions: (p.repetitions as number) ?? a?.repetitions,
          promptSystem: (p.promptSystem as string) ?? a?.promptSystem,
          promptUser: (p.promptUser as string) ?? a?.promptUser,
          // new cell -> clear the previous response until this one streams back
          responseText: cellChanged ? undefined : a?.responseText,
          done: d.progress?.done ?? a?.done ?? 0,
          total: d.progress?.total ?? a?.total ?? 0,
          since: Date.now(),
          lastEventAt: Date.now(),
        };
      });

    es.addEventListener('run-started', (ev) => bump('starting', parse(ev as MessageEvent)));
    es.addEventListener('cell-start', (ev) => bump('measuring', parse(ev as MessageEvent)));
    es.addEventListener('cell', (ev) => {
      const d = parse(ev as MessageEvent);
      if (d.progress) setProgress(d.progress);
      const resp = d.payload?.responseText as string | undefined;
      setActivity((a) =>
        a ? { ...a, done: d.progress?.done ?? a.done, responseText: resp ?? a.responseText, lastEventAt: Date.now() } : a,
      );
      refetchLive();
    });
    es.addEventListener('cell-aggregate', (ev) => {
      const d = parse(ev as MessageEvent);
      if (d.progress) setProgress(d.progress);
      bump('aggregating', d);
      refetchLive();
    });
    es.addEventListener('run-finished', (ev) => {
      const d = parse(ev as MessageEvent);
      // "interrupted" = the pod is shutting down; the run was re-queued and will
      // restart. Keep the UI in a live state instead of showing "finished".
      bump(
        (d.payload as { status?: string })?.status === 'interrupted' ? 'starting' : 'finished',
        d,
      );
      void refetchBatch();
      refetchLive();
    });
    es.addEventListener('error', () => {
      /* EventSource auto-reconnects; nothing to do */
    });
    return () => es.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRunId]);

  // Poll the batch + live matrix while a model is running (backstop for missed
  // SSE events, slow cells and reconnects after navigating away).
  useEffect(() => {
    if (!batch || batch.status !== 'running') return;
    const iv = window.setInterval(() => {
      void refetchBatch();
      doFetchLive();
    }, 5000);
    return () => window.clearInterval(iv);
  }, [batch, refetchBatch, doFetchLive]);

  // Seed the activity box when the partial matrix arrives but no stream event
  // has fired yet (e.g. right after reopening a running batch).
  useEffect(() => {
    if (!live || activity || batch?.currentRun?.status !== 'running') return;
    const total = axInput.length * axOutput.length;
    setActivity({
      phase: 'measuring',
      model: currentModel,
      done: live.cells.length,
      total,
      since: Date.now(),
      lastEventAt: Date.now(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, activity, batch?.currentRun?.status]);

  // --- auto-advance countdown (driven by the persisted batch settings, so it
  // also works after navigating away and for a toggle made mid-run) ---
  const batchAuto = batch?.autoAdvance ?? false;
  const batchDelay = batch?.delaySeconds ?? 5;
  useEffect(() => {
    if (batchAuto && currentFinished && !isLast && batch?.status === 'running') {
      setCountdown(batchDelay);
      const iv = window.setInterval(
        () => setCountdown((c) => (c == null ? null : c - 1)),
        1000,
      );
      return () => window.clearInterval(iv);
    }
    setCountdown(null);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFinished, isLast, batchAuto, batchDelay, batch?.status, batch?.currentIndex]);

  const patchTimer = useRef<number | null>(null);
  const patchBatch = (patch: { autoAdvance?: boolean; delaySeconds?: number }, debounce = false) => {
    if (!batch) return;
    setBatch({ ...batch, ...patch }); // optimistic
    const send = () =>
      api
        .updateBatch(batch.id, patch)
        .then(setBatch)
        .catch((e) => setError(e instanceof Error ? e.message : 'Failed to update run settings'));
    if (debounce) {
      if (patchTimer.current) window.clearTimeout(patchTimer.current);
      patchTimer.current = window.setTimeout(send, 400);
    } else {
      void send();
    }
  };

  useEffect(() => {
    if (countdown != null && countdown <= 0) {
      setCountdown(null);
      void goNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown]);

  const buildOverrides = (): MatrixOverrides | undefined => {
    if (!params) return undefined;
    const input = parseCsvInts(params.inputTokens);
    const output = parseCsvInts(params.outputTokens);
    return {
      ...(input.length ? { inputTokens: input } : {}),
      ...(output.length ? { outputTokens: output } : {}),
      repetitions: params.repetitions,
      safetyMargin: params.safetyMargin,
      colorMetric: params.colorMetric,
      maxKv: params.kvMode === 'fixed' ? params.kvValue : null,
    };
  };

  const start = async () => {
    setError(null);
    try {
      const state = await api.startBatch({
        models: [...selected],
        autoAdvance,
        delaySeconds,
        overrides: buildOverrides(),
      });
      sessionStorage.setItem(BATCH_KEY, state.id);
      setBatch(state);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start');
    }
  };

  const goNext = async () => {
    if (!batch) return;
    try {
      const state = await api.nextInBatch(batch.id);
      setBatch(state);
      setLive(null);
      setProgress(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to advance');
    }
  };

  const cancel = async () => {
    if (!batch) return;
    try {
      setBatch(await api.cancelBatch(batch.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to cancel');
    }
  };

  const reset = () => {
    sessionStorage.removeItem(BATCH_KEY);
    setBatch(null);
    setLive(null);
    setProgress(null);
  };

  const toggle = (name: string) =>
    setSelected((s) => {
      const next = new Set(s);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });

  const allSelected = models.length > 0 && selected.size === models.length;
  const batchDone = batch && batch.status !== 'running';

  // Fall back to counting the aggregated cells so the bar is right after reopening.
  const shownProgress = useMemo(
    () =>
      progress ??
      (live && axInput.length && axOutput.length
        ? { done: live.cells.length, total: axInput.length * axOutput.length }
        : null),
    [progress, live, axInput.length, axOutput.length],
  );
  const pct = useMemo(
    () =>
      shownProgress && shownProgress.total > 0
        ? Math.round((shownProgress.done / shownProgress.total) * 100)
        : 0,
    [shownProgress],
  );

  // ----------------------------- selection view -----------------------------
  if (!batch) {
    return (
      <section>
        <div className="page-head">
          <h1>Start a new benchmark run</h1>
        </div>
        {error ? <div className="banner error">{error}</div> : null}
        {catalogWarning ? (
          <div className="banner error">
            Could not load models from OpenWebUI: {catalogWarning}
          </div>
        ) : models.length === 0 ? (
          <div className="banner error">
            OpenWebUI returned no models. Check OPENWEBUI_BASE_URL and the (Bearer)
            API key and its model permissions.
          </div>
        ) : null}

        <label className="hardware-field">
          Benchmark hardware (Ollama host)
          <input
            type="text"
            placeholder="e.g. macOS · Apple M5 · 24 GB unified memory"
            value={hardwareDraft}
            onChange={(e) => setHardwareDraft(e.target.value)}
            onBlur={saveHardware}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
          />
          <span className="muted small">
            Shown with every result. Default comes from the chart; empty resets to it.
          </span>
        </label>

        <div className="run-options">
          <label className="check">
            <input
              type="checkbox"
              checked={autoAdvance}
              onChange={(e) => setAutoAdvance(e.target.checked)}
            />
            auto-advance
          </label>
          <label className="delay">
            Delay
            <input
              type="number"
              min={0}
              max={3600}
              value={delaySeconds}
              onChange={(e) => setDelaySeconds(Number(e.target.value))}
            />
            seconds
          </label>
          <button className="btn" disabled={selected.size === 0 || !params} onClick={start}>
            Run tests ({selected.size})
          </button>
        </div>

        {params && config ? (
          <details className="param-panel" open>
            <summary>Parameters</summary>
            <div className="param-grid">
              <label>
                Input tokens (columns)
                <input
                  value={params.inputTokens}
                  onChange={(e) => setParams({ ...params, inputTokens: e.target.value })}
                />
              </label>
              <label>
                Output tokens (rows)
                <input
                  value={params.outputTokens}
                  onChange={(e) => setParams({ ...params, outputTokens: e.target.value })}
                />
              </label>
              <label>
                Repetitions / cell
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={params.repetitions}
                  onChange={(e) =>
                    setParams({ ...params, repetitions: Math.max(1, Number(e.target.value)) })
                  }
                />
              </label>
              <label>
                Safety margin (tokens)
                <input
                  type="number"
                  min={0}
                  max={32768}
                  value={params.safetyMargin}
                  onChange={(e) =>
                    setParams({ ...params, safetyMargin: Math.max(0, Number(e.target.value)) })
                  }
                />
              </label>
              <label>
                Colour metric
                <select
                  value={params.colorMetric}
                  onChange={(e) =>
                    setParams({ ...params, colorMetric: e.target.value as ColorMetric })
                  }
                >
                  {(
                    ['generate_tps', 'think_tps', 'prefill_tps', 'ttft_ms'] as ColorMetric[]
                  ).map((m) => (
                    <option key={m} value={m}>
                      {METRIC_LABEL[m]}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                KV limit
                <select
                  value={params.kvMode}
                  onChange={(e) =>
                    setParams({ ...params, kvMode: e.target.value as 'auto' | 'fixed' })
                  }
                >
                  <option value="auto">automatic (per model, from OpenWebUI)</option>
                  <option value="fixed">fixed value…</option>
                </select>
                {params.kvMode === 'fixed' ? (
                  <input
                    type="number"
                    min={256}
                    max={2000000}
                    value={params.kvValue}
                    onChange={(e) =>
                      setParams({ ...params, kvValue: Math.max(256, Number(e.target.value)) })
                    }
                  />
                ) : null}
              </label>
            </div>
            <div className="param-actions">
              <span className="muted small">
                Cells where input + output + margin exceed the KV limit are skipped.
              </span>
              <button
                className="btn ghost small"
                onClick={() => setParams(defaultParams(config))}
              >
                Reset to defaults
              </button>
            </div>
          </details>
        ) : null}

        <table className="table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) =>
                    setSelected(e.target.checked ? new Set(models.map((m) => m.name)) : new Set())
                  }
                />
              </th>
              <th>Model</th>
              <th>Max KV</th>
              <th>Last tested</th>
              <th>Last result</th>
            </tr>
          </thead>
          <tbody>
            {models.map((m) => (
              <tr key={m.name} className={selected.has(m.name) ? 'row-selected' : ''}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.has(m.name)}
                    onChange={() => toggle(m.name)}
                  />
                </td>
                <td>
                  {m.displayName}
                  <div className="muted small">
                    {m.name} · KV source: {m.contextSource ?? '–'}
                  </div>
                </td>
                <td>{m.maxKv ?? '–'}</td>
                <td>{m.lastTestedAt ? new Date(m.lastTestedAt).toLocaleString() : 'never'}</td>
                <td>
                  {m.lastStatus ?? '–'}
                  {m.lastScore != null ? ` · ${m.lastScore.toFixed(1)}` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    );
  }

  // ------------------------------- run view --------------------------------
  return (
    <section>
      <div className="page-head">
        <h1>Benchmark run</h1>
        <div className="run-actions">
          {!batchDone ? (
            <button className="btn ghost" onClick={cancel}>
              Cancel
            </button>
          ) : (
            <button className="btn" onClick={reset}>
              New selection
            </button>
          )}
        </div>
      </div>
      {error ? <div className="banner error">{error}</div> : null}

      <ModelRibbon models={batch.models} currentIndex={batch.currentIndex} />

      <div className="run-status-bar">
        <div>
          Model {Math.min(batch.currentIndex + 1, batch.models.length)} / {batch.models.length}
          {currentModel ? ` · ${currentModel}` : ''} ·{' '}
          <strong>{batch.currentRun?.status ?? '—'}</strong>
        </div>
        <div className="progress">
          <div className="progress-fill" style={{ width: `${pct}%` }} />
          <span>
            {shownProgress ? `${shownProgress.done}/${shownProgress.total} cells` : 'starting…'}
          </span>
        </div>
        {batch.status === 'running' ? (
          <div className="run-auto">
            <label className="check">
              <input
                type="checkbox"
                checked={batch.autoAdvance}
                onChange={(e) => patchBatch({ autoAdvance: e.target.checked })}
              />
              auto-advance
            </label>
            <label className="delay">
              delay
              <input
                type="number"
                min={0}
                max={3600}
                value={batch.delaySeconds}
                onChange={(e) =>
                  patchBatch({ delaySeconds: Math.max(0, Number(e.target.value)) }, true)
                }
              />
              s
            </label>
          </div>
        ) : null}
        <div className="run-next">
          {countdown != null ? (
            <span className="countdown">auto-advance in {Math.max(0, countdown)}s</span>
          ) : null}
          <button
            className="btn"
            disabled={!currentFinished || isLast || batch.status !== 'running'}
            onClick={goNext}
          >
            Next →
          </button>
          {isLast && currentFinished ? (
            <span className="badge ok">all models tested</span>
          ) : null}
        </div>
      </div>

      {batch.currentRun?.status === 'error' ? (
        <div className="banner error">
          This model failed{live?.error ? `: ${live.error}` : ''}. See the pod logs; the run
          walkthrough can continue with “Next”.
        </div>
      ) : live && live.summary && live.summary.cellsErrored > 0 ? (
        <div className="banner error">
          {live.summary.cellsErrored} of {live.summary.cellsTotal} cells failed (⚠ in the matrix).
        </div>
      ) : null}

      {/* The criteria panel + matrix are always visible during a run, even
          before the first measurement lands. */}
      <PhasePanel summary={live?.summary ?? null} />
      <div className="matrix-row">
        <Heatmap
          inputTokens={axInput}
          outputTokens={axOutput}
          cells={live?.cells ?? []}
          colorMetric={live?.colorMetric ?? config?.matrix.colorMetric ?? 'generate_tps'}
          valueRange={live?.valueRange ?? { min: null, max: null }}
          active={
            activity && activity.phase !== 'finished'
              ? { input: activity.input, output: activity.output }
              : null
          }
        />
        {batch.status === 'running' ? <RunActivity activity={activity} /> : null}
      </div>
      <MatrixTimingTable inputTokens={axInput} outputTokens={axOutput} cells={live?.cells ?? []} />
    </section>
  );
}
